import { type RefObject, useCallback, useLayoutEffect } from 'react';

/**
 * Whether anything is scrolled under the left or right pinned columns, kept
 * as flags on the scroller for CSS (`data-shadow-left` / `-right`), not as
 * state: a re-render of every row on each change made the shadows lag the
 * scroll. Returns the update for the scroller's `onScroll`; resizing the
 * scroller or its table updates them too.
 */
export function usePinShadows(
  scrollRef: RefObject<HTMLElement | null>
): () => void {
  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // These attributes are also targeted by src/index.css.
    el.toggleAttribute('data-shadow-left', el.scrollLeft > 0);
    el.toggleAttribute(
      'data-shadow-right',
      el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    );
  }, [scrollRef]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    update();
    // The scroller's own size follows the window; the table's follows the
    // columns. Either can bring a pinned edge into or out of view.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [scrollRef, update]);

  return update;
}
